<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { reportPeriodUnix } from 'dashboard/helper/reportPeriod';
import { useReportTimezone } from 'dashboard/composables/useReportTimezone';
import subDays from 'date-fns/subDays';
import differenceInDays from 'date-fns/differenceInDays';
import ActiveFilterChip from './Filters/v3/ActiveFilterChip.vue';
import ReportFilterPanel from './ReportFilterPanel.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { GROUP_BY_FILTER } from '../constants';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useShellPeriod } from '../composables/useShellPeriod';
import { rangeLabelKey } from '../helpers/reportsPeriod';

const props = defineProps({
  filterType: {
    type: String,
    required: false,
    default: '',
    validator: value =>
      ['teams', 'inboxes', 'labels', 'agents', ''].includes(value),
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  showGroupBy: {
    type: Boolean,
    default: true,
  },
  showBusinessHours: {
    type: Boolean,
    default: true,
  },
  showEntityFilter: {
    type: Boolean,
    default: true,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['filterChange', 'retry']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { isWorkspaceV2 } = useWorkspaceV2();

const buildReportFilterList = (items, type) => {
  if (!Array.isArray(items)) return [];

  return items.map(item => ({
    id: item.id,
    name: item.name || item.title,
    type,
  }));
};

const getReportFilterKey = filterType => {
  const keyMap = {
    teams: 'team_id',
    inboxes: 'inbox_id',
    labels: 'label_id',
    agents: 'agent_id',
  };
  return keyMap[filterType] || '';
};

const getFilterKey = () => getReportFilterKey(props.filterType);

const showSubDropdownMenu = ref(false);
const showGroupByDropdown = ref(false);
const activeFilterType = ref('');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
const groupBy = ref(GROUP_BY_FILTER[1]);
const groupByfilterItemsList = ref([{ id: 1, name: 'Day' }]);

const appliedFilters = ref(
  props.showEntityFilter
    ? { [getFilterKey()]: props.selectedItem?.id || null }
    : {}
);

const filterSource = computed(() => {
  const sources = {
    teams: store.getters['teams/getTeams'],
    inboxes: store.getters['inboxes/getInboxes'],
    labels: store.getters['labels/getLabels'],
    agents: store.getters['agents/getAgents'],
  };
  return sources[props.filterType] || [];
});

// Horario comercial so faz sentido quando ao menos 1 inbox do escopo tem
// working_hours_enabled; caso contrario o toggle so produz metricas zeradas.
const hasBusinessHoursInbox = computed(() =>
  (store.getters['inboxes/getInboxes'] || []).some(
    inbox => inbox.working_hours_enabled
  )
);

const canShowBusinessHours = computed(
  () => props.showBusinessHours && hasBusinessHoursInbox.value
);

// Bordas do periodo no fuso da CONTA: o dia escolhido e o dia da conta,
// igual para qualquer navegador.
const { reportTimezone } = useReportTimezone();
const reportPeriod = computed(() =>
  reportPeriodUnix({
    dates: customDateRange.value,
    rangeType: selectedDateRange.value,
    timeZone: reportTimezone.value,
  })
);
const from = computed(() => reportPeriod.value.from);
const to = computed(() => reportPeriod.value.to);

const daysDifference = computed(() => {
  return differenceInDays(customDateRange.value[1], customDateRange.value[0]);
});

// Granularidade dia/semana/mes deve estar SEMPRE disponivel quando o
// seletor esta habilitado; o gate antigo (>= 29 dias) escondia o chip em
// intervalos curtos. A restricao de 'hora' fica na matriz fetchFilterItems
// (so em intervalos curtos) para nao estourar o eixo do grafico.
const isGroupByPossible = computed(() => {
  return props.showGroupBy;
});

const GROUP_BY_OPTIONS = computed(() => ({
  // Intervalo muito curto (<= 2 dias): permite Hora + Dia.
  SHORT: [
    { id: 5, name: t('REPORT.GROUPING_OPTIONS.HOUR') },
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
  ],
  // Ate ~1 mes: Dia + Semana.
  WEEK: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  ],
  // Ate ~1 ano: Dia + Semana + Mes.
  MONTH: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
  ],
  // ~1 ano: Dia + Semana + Mes + Ano.
  MONTH_YEAR: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
    { id: 4, name: t('REPORT.GROUPING_OPTIONS.YEAR') },
  ],
  // Acima de 1 ano: Dia vira granular demais; Semana + Mes + Ano.
  YEAR: [
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
    { id: 4, name: t('REPORT.GROUPING_OPTIONS.YEAR') },
  ],
}));

const fetchFilterItems = () => {
  const days = daysDifference.value;
  if (days >= 364) return GROUP_BY_OPTIONS.value.YEAR;
  if (days >= 90) return GROUP_BY_OPTIONS.value.MONTH_YEAR;
  if (days >= 29) return GROUP_BY_OPTIONS.value.MONTH;
  if (days > 2) return GROUP_BY_OPTIONS.value.WEEK;
  // <= 2 dias: hora faz sentido sem estourar o eixo do grafico.
  return GROUP_BY_OPTIONS.value.SHORT;
};

const filterOptions = computed(() =>
  buildReportFilterList(filterSource.value, props.filterType)
);

const filterPlaceholder = computed(() => {
  const placeholders = {
    teams: 'TEAM_REPORTS.FILTERS.INPUT_PLACEHOLDER.TEAMS',
    inboxes: 'INBOX_REPORTS.FILTERS.INPUT_PLACEHOLDER.INBOXES',
    labels: 'LABEL_REPORTS.FILTERS.INPUT_PLACEHOLDER.LABELS',
    agents: 'AGENT_REPORTS.FILTERS.INPUT_PLACEHOLDER.AGENTS',
  };
  return t(placeholders[props.filterType] || '');
});

const defaultFilterLabel = computed(() => {
  const labelKeys = {
    teams: 'TEAM_REPORTS.FILTER_DROPDOWN_LABEL',
    inboxes: 'INBOX_REPORTS.FILTER_DROPDOWN_LABEL',
    labels: 'LABEL_REPORTS.FILTER_DROPDOWN_LABEL',
    agents: 'AGENT_REPORTS.FILTER_DROPDOWN_LABEL',
  };
  return t(labelKeys[props.filterType] || 'FORMS.MULTISELECT.SELECT_ONE');
});

const selectedFilterName = computed(() => {
  const filterKey = getFilterKey();
  const selectedId = appliedFilters.value[filterKey];

  if (!selectedId) {
    return defaultFilterLabel.value;
  }

  const selectedItem = filterOptions.value.find(item => item.id === selectedId);
  return selectedItem?.name || defaultFilterLabel.value;
});

const selectedRangeLabel = computed(() =>
  t(rangeLabelKey(selectedDateRange.value))
);

const currentGroupByName = computed(
  () =>
    groupByfilterItemsList.value.find(item => item.id === groupBy.value.id)
      ?.name || t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')
);

const filterContext = computed(() => {
  const context = [selectedRangeLabel.value];
  if (props.showEntityFilter && appliedFilters.value[getFilterKey()]) {
    context.unshift(selectedFilterName.value);
  }
  if (isGroupByPossible.value) {
    context.push(
      t('REPORT.FILTER_PANEL.GROUPED_BY', { value: currentGroupByName.value })
    );
  }
  if (businessHoursSelected.value) {
    context.push(t('REPORT.BUSINESS_HOURS'));
  }
  return context.join(' · ');
});

const canReset = computed(
  () =>
    selectedDateRange.value !== DATE_RANGE_TYPES.LAST_7_DAYS ||
    groupBy.value.id !== GROUP_BY_FILTER[1].id ||
    businessHoursSelected.value
);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHoursSelected.value,
    groupBy: isGroupByPossible.value ? groupBy.value.id : null,
    range: selectedDateRange.value,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  const payload = {
    from: from.value,
    to: to.value,
    businessHours: businessHoursSelected.value,
  };

  if (props.showGroupBy) {
    // Always emit groupBy, default to day when range is too short
    payload.groupBy = isGroupByPossible.value
      ? groupBy.value
      : GROUP_BY_FILTER[1];
  }

  if (props.showEntityFilter) {
    const filterKey = getFilterKey();
    const selectedValue = appliedFilters.value[filterKey];

    if (selectedValue) {
      payload[props.filterType] =
        props.filterType === 'agents'
          ? [{ id: selectedValue }]
          : { id: selectedValue };
    }
  }

  updateURLParams();
  emit('filterChange', payload);
};

const closeActiveFilterDropdown = () => {
  showSubDropdownMenu.value = false;
  activeFilterType.value = '';
};

const openActiveFilterDropdown = filterType => {
  showGroupByDropdown.value = false;
  activeFilterType.value = filterType;
  showSubDropdownMenu.value = !showSubDropdownMenu.value;
};

const addFilter = item => {
  const filterKey = getFilterKey();
  appliedFilters.value[filterKey] = item.id;
  closeActiveFilterDropdown();
  emitChange();

  // Navigate to the new entity's route
  const routeNameMap = {
    teams: 'team_reports_show',
    inboxes: 'inbox_reports_show',
    labels: 'label_reports_show',
    agents: 'agent_reports_show',
  };

  const routeName = routeNameMap[props.filterType];
  if (routeName) {
    router.push({
      name: routeName,
      params: { ...route.params, id: item.id },
      query: route.query,
    });
  }
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  groupByfilterItemsList.value = fetchFilterItems();
  const filterItems = groupByfilterItemsList.value.filter(
    item => item.id === groupBy.value.id
  );
  if (filterItems.length === 0) {
    groupBy.value = GROUP_BY_FILTER[groupByfilterItemsList.value[0].id];
  }
  emitChange();
};

// Dentro da casa dos relatorios o periodo vem do seletor unico da casca: o
// calendario desta tela some e ela segue a troca feita la.
const shellPeriod = useShellPeriod(next => {
  if (
    next.range === selectedDateRange.value &&
    next.from === from.value &&
    next.to === to.value
  )
    return;
  onDateRangeChange([next.startDay, next.endDay, next.range]);
});

const onBusinessHoursToggle = () => {
  emitChange();
};

const onGroupByFilterChange = payload => {
  groupBy.value = GROUP_BY_FILTER[payload.id];
  showGroupByDropdown.value = false;
  emitChange();
};

const resetFilters = () => {
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  selectedDateRange.value = DATE_RANGE_TYPES.LAST_7_DAYS;
  businessHoursSelected.value = false;
  groupBy.value = GROUP_BY_FILTER[1];
  groupByfilterItemsList.value = fetchFilterItems();
  closeActiveFilterDropdown();
  showGroupByDropdown.value = false;
  emitChange();
};

const toggleGroupByDropdown = () => {
  showGroupByDropdown.value = !showGroupByDropdown.value;
};

const closeGroupByDropdown = () => {
  showGroupByDropdown.value = false;
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }

  if (urlParams.groupBy) {
    const groupByValue = GROUP_BY_FILTER[urlParams.groupBy];
    if (groupByValue) {
      groupBy.value = groupByValue;
    }
  }

  // Initialize entity filter from route params (not URL query)
  if (props.showEntityFilter && route.params.id) {
    const filterKey = getFilterKey();
    appliedFilters.value[filterKey] = Number(route.params.id);
  }
};

onMounted(() => {
  initializeFromURL();
  // Se o toggle nao pode ser exibido (nenhuma inbox com horario), garantir que
  // um businessHours=true vindo da URL nao zere as metricas silenciosamente.
  if (!canShowBusinessHours.value) {
    businessHoursSelected.value = false;
  }
  groupByfilterItemsList.value = fetchFilterItems();
  emitChange();
});
</script>

<template>
  <ReportFilterPanel
    :context="filterContext"
    :can-reset="canReset"
    :error-message="errorMessage"
    @reset="resetFilters"
    @retry="emit('retry')"
  >
    <div class="flex flex-col w-full gap-3 lg:flex-row">
      <label
        v-if="!shellPeriod"
        :class="
          isWorkspaceV2
            ? 'flex flex-col gap-1.5 text-xs font-medium text-n-slate-11'
            : 'contents'
        "
      >
        <span v-if="isWorkspaceV2">{{ $t('REPORT.FILTER_PANEL.PERIOD') }}</span>
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
      </label>

      <div
        class="flex gap-2 items-center w-full"
        :class="{ 'flex-wrap !items-end': isWorkspaceV2 }"
      >
        <ActiveFilterChip
          v-if="showEntityFilter"
          :id="appliedFilters[getFilterKey()]"
          :name="selectedFilterName"
          :type="filterType"
          :options="filterOptions"
          :active-filter-type="activeFilterType"
          :show-menu="showSubDropdownMenu"
          :placeholder="filterPlaceholder"
          :show-clear-filter="false"
          enable-search
          @toggle-dropdown="openActiveFilterDropdown"
          @close-dropdown="closeActiveFilterDropdown"
          @add-filter="addFilter"
        />

        <ActiveFilterChip
          v-if="isGroupByPossible"
          :id="groupBy?.id"
          :name="
            groupByfilterItemsList.find(item => item.id === groupBy?.id)
              ?.name || $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')
          "
          type="groupBy"
          :options="groupByfilterItemsList"
          :active-filter-type="showGroupByDropdown ? 'groupBy' : ''"
          :show-menu="showGroupByDropdown"
          :placeholder="$t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')"
          :enable-search="false"
          :show-clear-filter="false"
          @toggle-dropdown="toggleGroupByDropdown"
          @close-dropdown="closeGroupByDropdown"
          @add-filter="onGroupByFilterChange"
          @remove-filter="() => {}"
        />

        <label
          v-if="canShowBusinessHours"
          class="flex items-center flex-shrink-0 text-sm whitespace-nowrap ltr:ml-auto rtl:mr-auto"
          :class="{ 'min-h-8 gap-2': isWorkspaceV2 }"
        >
          <span :class="{ 'mx-2': !isWorkspaceV2 }">
            {{ $t('REPORT.BUSINESS_HOURS') }}
          </span>
          <ToggleSwitch
            v-model="businessHoursSelected"
            @change="onBusinessHoursToggle"
          />
        </label>
      </div>
    </div>
  </ReportFilterPanel>
</template>
